import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DIALOG_RETURN_TYPES,
  DialogService,
} from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Store } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, of, startWith, Subscription, take, throwError } from 'rxjs';

import { selectManualCropAids } from '../../../../advices/store/selectors';
import { selectCropYear } from '../../../../store/selectors';
import { CropAid, CropAidResponse, ProductStockData, SubmittedProductStockData } from '../../../models';
import { OrderplanningService } from '../../../services/orderplanning.service';

export type CurrentStockDialogData = AbstractDialogData;

interface StockProduct {
  id: number;
  name: string;
  admissionNumbers: string[];
  unitOfMeasurement: string;
  isActive: boolean;
}

interface StockRow {
  product: StockProduct;
  quantity: FormControl<number | null>;
}

export const getDefaultStockCropYear = (date: Date): number =>
  date.getMonth() >= 8 ? date.getFullYear() + 1 : date.getFullYear();

export const getAvailableStockCropYears = (date: Date): number[] => {
  const currentYear = date.getFullYear();
  return Array.from({ length: 5 }, (_, index) => currentYear - 3 + index);
};

const stockQuantityValidator: ValidatorFn = (control) => {
  const amount = control.value;

  return typeof amount === 'number' && Number.isFinite(amount) && amount >= 0 && Math.round(amount * 100) / 100 === amount
    ? null
    : { stockQuantity: true };
};

@Component({
  standalone: true,
  selector: 'app-current-stock-dialog',
  templateUrl: './current-stock-dialog.component.html',
  styleUrls: ['./current-stock-dialog.component.scss'],
  imports: [
    MatAutocompleteModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinner,
    MatSelectModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentStockDialogComponent extends AbstractDialog<void> implements OnInit {
  readonly data = inject<CurrentStockDialogData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef =
    inject<MatDialogRef<CurrentStockDialogComponent, AbstractDialogResultData<void>>>(MatDialogRef);

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(DialogService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly locale = inject(LocaleService);
  private readonly orderplanningService = inject(OrderplanningService);
  private readonly store = inject(Store);

  private readonly allProducts = signal<StockProduct[]>([]);
  private readonly hasPersistedStock = signal(false);
  private stockLoadSubscription?: Subscription;
  private highlightTimeout?: ReturnType<typeof setTimeout>;

  protected readonly availableYears = getAvailableStockCropYears(new Date());
  protected readonly cropYearControl = new FormControl(getDefaultStockCropYear(new Date()), { nonNullable: true });
  protected readonly hasUnsavedChanges = signal(false);
  protected readonly highlightedProductId = signal<number | null>(null);
  protected readonly stockLoadFailed = signal(false);
  protected readonly loading = signal(true);
  protected readonly saving = signal(false);
  protected readonly searchControl = new FormControl<string | StockProduct>('', { nonNullable: true });
  protected readonly selectedYear = signal(this.cropYearControl.value);
  protected readonly stockRows = signal<StockRow[]>([]);

  protected readonly isPastCropYear = computed(() => this.selectedYear() < new Date().getFullYear());
  private readonly searchValue = signal<string | StockProduct>('');
  private readonly searchableProducts = computed(() =>
    this.allProducts()
      .filter((product) => product.isActive)
      .sort((first, second) => first.name.localeCompare(second.name, undefined, { sensitivity: 'base' })),
  );

  protected readonly filteredProducts = computed(() => {
    const value = this.searchValue();
    const searchTerm = typeof value === 'string' ? value.trim().toLocaleLowerCase() : '';

    return this.searchableProducts().filter(
      (product) => searchTerm.length === 0 || product.name.toLocaleLowerCase().includes(searchTerm),
    );
  });

  protected readonly productCountTranslationKey = computed(() =>
    this.stockRows().length === 1 ? 'shared.stockEntry.productCountSingle' : 'shared.stockEntry.productCountMultiple',
  );

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(startWith(this.searchControl.value), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.searchValue.set(value));

    this.loadInitialData();
  }

  protected displayProduct(product: StockProduct | string): string {
    return typeof product === 'string' ? product : product.name;
  }

  protected formatProduct(product: StockProduct): string {
    const admissions = product.admissionNumbers.length > 0 ? ` (${product.admissionNumbers.join(', ')})` : '';
    return `${product.name}${admissions}`;
  }

  protected onProductSelected(event: MatAutocompleteSelectedEvent): void {
    const product = event.option.value as StockProduct;
    const productAlreadyInStock = this.stockRows().some((row) => row.product.id === product.id);

    this.searchControl.setValue('');

    if (productAlreadyInStock) {
      this.focusAndHighlightProduct(product.id);
      return;
    }

    const row = this.createStockRow(product, null);
    this.stockRows.update((rows) => [...rows, row]);
    this.hasUnsavedChanges.set(true);
    this.focusAndHighlightProduct(product.id);
  }

  protected removeProduct(productId: number): void {
    this.stockRows.update((rows) => rows.filter((row) => row.product.id !== productId));
    this.hasUnsavedChanges.set(true);
  }

  override cancel(result?: void): void {
    if (!this.hasUnsavedChanges()) {
      super.cancel(result);
      return;
    }

    this.dialog
      .pendingChanges()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ status }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          super.cancel(result);
        } else {
          this.canceling = false;
        }
      });
  }

  protected requestYearChange(year: number): void {
    if (year === this.selectedYear()) {
      return;
    }

    if (!this.hasUnsavedChanges()) {
      this.loadStock(year);
      return;
    }

    this.dialog
      .pendingChanges()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ status }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          this.loadStock(year);
        } else {
          this.cropYearControl.setValue(this.selectedYear(), { emitEvent: false });
        }
      });
  }

  protected saveStock(): void {
    const rows = this.stockRows();

    if (this.isPastCropYear() || (rows.length === 0 && !this.hasPersistedStock())) {
      return;
    }

    if (rows.some((row) => row.quantity.invalid)) {
      rows.forEach((row) => row.quantity.markAsTouched());
      return;
    }

    const productInventories = rows.reduce<ProductStockData[]>((inventories, row) => {
      const amount = row.quantity.value;

      if (amount !== null) {
        inventories.push({ cropAidId: row.product.id, amount });
      }

      return inventories;
    }, []);

    this.saving.set(true);
    this.orderplanningService
      .upsertSubmittedProductInventory(this.selectedYear(), productInventories)
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          const count = productInventories.length;
          const messageKey =
            count === 1 ? 'shared.stockEntry.saveSuccessSingle' : 'shared.stockEntry.saveSuccessMultiple';
          this.store.dispatch(
            NotificationActions.successSnackbar({
              message: this.locale.translate(messageKey, { count: count.toString() }),
            }),
          );
          this.hasPersistedStock.set(productInventories.length > 0);
          this.hasUnsavedChanges.set(false);
        },
        error: () => this.showError('shared.stockEntry.saveError'),
      });
  }

  protected isSaveDisabled(): boolean {
    return (
      this.isPastCropYear() ||
      !this.hasUnsavedChanges() ||
      this.loading() ||
      this.saving() ||
      this.stockLoadFailed() ||
      (this.stockRows().length === 0 && !this.hasPersistedStock()) ||
      this.stockRows().some((row) => row.quantity.invalid)
    );
  }

  private loadInitialData(): void {
    this.loadProductCatalog();
    this.store
      .select(selectCropYear)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((cropYear) => {
        this.selectedYear.set(cropYear);
        this.cropYearControl.setValue(cropYear, { emitEvent: false });
        this.loadStock(cropYear);
      });
  }

  private loadProductCatalog(): void {
    this.store
      .select(selectManualCropAids)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((loadedCropAids) => {
        if (loadedCropAids.length > 0) {
          this.setProductCatalog(
            loadedCropAids
              .filter((cropAid) => cropAid.isCropProtection)
              .map((cropAid) => this.mapCropAid(cropAid)),
          );
          return;
        }

        this.fetchProductCatalog();
      });
  }

  private fetchProductCatalog(): void {
    this.orderplanningService
      .getCropAids(1)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (cropAids) => this.setProductCatalog(cropAids.map((cropAid) => this.mapCropAid(cropAid))),
        error: () => this.showError('shared.stockEntry.loadError'),
      });
  }

  private setProductCatalog(products: StockProduct[]): void {
    this.allProducts.set(products);
    this.refreshStockRowProducts();
  }

  private loadStock(year: number): void {
    this.stockLoadSubscription?.unsubscribe();
    this.loading.set(true);

    this.stockLoadSubscription = this.orderplanningService
      .getSubmittedProductInventory(year)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          error.status === 404 ? of({ productInventories: [] }) : throwError(() => error),
        ),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (submittedStock) => {
          this.stockLoadFailed.set(false);
          this.selectedYear.set(year);
          this.cropYearControl.setValue(year, { emitEvent: false });
          this.setSubmittedStockRows(submittedStock.productInventories);
        },
        error: () => {
          this.stockLoadFailed.set(true);
          this.cropYearControl.setValue(this.selectedYear(), { emitEvent: false });
          this.showError('shared.stockEntry.loadError');
        },
      });
  }

  private mapCropAid(cropAid: CropAid | CropAidResponse): StockProduct {
    const admissions =
      'codes' in cropAid
        ? cropAid.codes
        : (cropAid.cropProtection?.admissions?.map((admission) => admission.number) ?? []);
    const admissionNumbers =
      admissions.length > 0
        ? admissions
        : (cropAid.cropProtection?.admissionNumbers
          ?.split(',')
          .map((admissionNumber) => admissionNumber.trim())
          .filter(Boolean) ?? []);

    return {
      id: cropAid.id,
      name: cropAid.name,
      admissionNumbers,
      unitOfMeasurement: cropAid.unitOfMeasurement,
      isActive: cropAid.isActive,
    };
  }

  private setSubmittedStockRows(productInventories: SubmittedProductStockData[]): void {
    const productsById = new Map(this.allProducts().map((product) => [product.id, product]));
    const rows = productInventories.map((inventory) => {
      const product = productsById.get(inventory.cropAidId) ?? {
        id: inventory.cropAidId,
        name: inventory.name,
        admissionNumbers: inventory.admissionNumbers,
        unitOfMeasurement: inventory.unitOfMeasurement,
        isActive: false,
      };

      return this.createStockRow(product, inventory.amount);
    });

    this.stockRows.set(rows);
    this.hasPersistedStock.set(rows.length > 0);
    this.hasUnsavedChanges.set(false);
  }

  private refreshStockRowProducts(): void {
    const productsById = new Map(this.allProducts().map((product) => [product.id, product]));

    this.stockRows.update((rows) =>
      rows.map((row) => ({
        ...row,
        product: productsById.get(row.product.id) ?? row.product,
      })),
    );
  }

  private createStockRow(product: StockProduct, amount: number | null): StockRow {
    const quantity = new FormControl(amount, {
      validators: [Validators.required, stockQuantityValidator],
    });

    if (amount !== null && amount < 0) {
      quantity.markAsTouched();
    }

    quantity.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.hasUnsavedChanges.set(true);

      if (value !== null && value < 0) {
        quantity.markAsTouched();
      }
    });

    return { product, quantity };
  }

  private focusAndHighlightProduct(productId: number): void {
    this.highlightedProductId.set(productId);
    globalThis.clearTimeout(this.highlightTimeout);

    setTimeout(() => {
      const input = this.elementRef.nativeElement.querySelector<HTMLInputElement>(
        `[data-stock-product-id="${productId}"] input`,
      );
      input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input?.focus();
    });

    this.highlightTimeout = setTimeout(() => this.highlightedProductId.set(null), 1500);
  }

  private showError(messageKey: string): void {
    this.store.dispatch(
      NotificationActions.errorSnackbar({
        message: this.locale.translate(messageKey),
      }),
    );
  }
}
